import type { JSX } from 'react';
import { TakiIcon } from '@taki/ui';

/**
 * Ecran de demarrage (boot screen) du shell.
 *
 * Plein ecran, fond bg, logo Taki centre avec une respiration discrete
 * (pulse). Affiche UNIQUEMENT au tout premier boot : le rendu d'attente
 * d'AuthGuard, le temps que la config se charge et que l'auth se resolve. Une
 * fois l'auth resolue une premiere fois, AuthGuard verrouille le boot (voir le
 * verrou `bootedRef` dans AuthGuard.tsx) : ce composant ne reapparait JAMAIS
 * ensuite, meme lors d'une re-signature.
 *
 * En amont, le splash HTML de l'hote (apps/hello/index.html) affiche le MEME
 * visuel avant meme le premier rendu React : la sequence splash HTML -> boot
 * screen React -> app s'enchaine sans flash blanc.
 *
 * Reduced motion : logo statique, pleine opacite (pas de pulse).
 */

const BOOT_CSS = `
.taki-boot__mark {
  animation: taki-boot-pulse calc(var(--dur-slow) * 5) var(--ease-standard) infinite;
}
@keyframes taki-boot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.97); }
}
@media (prefers-reduced-motion: reduce) {
  .taki-boot__mark {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
`;

/**
 * Splash de demarrage. Aucune prop. `role="status"` + libelle pour annoncer le
 * chargement aux lecteurs d'ecran ; le logo lui-meme est masque de l'AT
 * (aria-hidden) pour ne pas doubler l'annonce.
 */
export function BootScreen(): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Chargement"
      className="flex min-h-screen w-full items-center justify-center bg-bg"
    >
      <style>{BOOT_CSS}</style>
      {/* 56 : hauteur du logo au boot. currentColor -> l'icone suit l'encre du
          theme via text-ink-900. */}
      <TakiIcon aria-hidden className="taki-boot__mark h-[3.5rem] w-auto text-ink-900" />
    </div>
  );
}
